import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../contexts/ApiContext';
import { NavigateNext, SmartToy, CheckCircle, Cancel } from '@mui/icons-material';
import GenericEntityEditor from '../../components/Common/GenericEntityEditor';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bot-tabpanel-${index}`}
      aria-labelledby={`bot-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const BotDetails: React.FC = () => {
  const { botId } = useParams<{ botId: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const [currentTab, setCurrentTab] = useState(0);

  // Fetch bot details
  const { data: botResponse, isLoading, error } = useQuery({
    queryKey: ['bot', botId],
    queryFn: async () => {
      const response = await api.get(`/database/bot/${botId}/raw`);
      return response;
    },
    enabled: !!botId,
  });

  // Extract bot data from dynamo
  const bot = botResponse?.dynamo || botResponse;

  // Fetch user details if userId exists
  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['user', bot?.userId],
    queryFn: async () => {
      if (!bot?.userId) return null;
      const response = await api.get(`/users/${bot.userId}`);
      return response;
    },
    enabled: !!bot?.userId,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !bot) {
    return (
      <Alert severity="error">
        Failed to load bot details. {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNext fontSize="small" />} 
        sx={{ mb: 3 }}
      >
        <Link
          component={RouterLink}
          to="/bots"
          underline="hover"
          color="inherit"
        >
          Bots
        </Link>
        <Typography color="text.primary">{bot.name || botId}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <SmartToy sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box flex={1}>
            <Typography variant="h4" gutterBottom>
              {bot.name || 'Unnamed Bot'}
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Chip 
                label={bot.paperTrading ? 'Paper Trading' : 'Live Trading'} 
                color={bot.paperTrading ? 'warning' : 'success'}
                size="small"
              />
              <Chip 
                label={bot.isActive ? 'Active' : 'Inactive'} 
                icon={bot.isActive ? <CheckCircle /> : <Cancel />}
                color={bot.isActive ? 'success' : 'default'}
                size="small"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ID: {botId}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" />
          <Tab label="Advanced DB" />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            {/* General Information */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    General Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Bot Name
                      </Typography>
                      <Typography variant="body1">
                        {bot.name || 'N/A'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        User
                      </Typography>
                      {bot.userId ? (
                        <Link
                          component={RouterLink}
                          to={`/users/${bot.userId}`}
                          underline="hover"
                          sx={{
                            display: 'block',
                            '&:hover': { color: 'primary.main' }
                          }}
                        >
                          <Typography variant="body1" fontWeight={500}>
                            {userLoading ? (
                              <CircularProgress size={16} sx={{ ml: 1 }} />
                            ) : (
                              userData?.email || userData?.user?.email || 'Unknown User'
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {bot.userId}
                          </Typography>
                        </Link>
                      ) : (
                        <Typography variant="body1">N/A</Typography>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Exchange
                      </Typography>
                      <Typography variant="body1">
                        {bot.exchangeId === '1' ? 'Binance' : bot.exchangeId === '2' ? 'Coinbase' : bot.exchangeId === '3' ? 'Kraken' : bot.exchangeId || 'N/A'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Bot Type
                      </Typography>
                      <Typography variant="body1">
                        {bot.typeId === '1' ? 'Market Maker' : bot.typeId === '2' ? 'Arbitrage' : bot.typeId === '3' ? 'Trend Follower' : bot.typeId || 'N/A'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Trading Mode
                      </Typography>
                      <Typography variant="body1">
                        {bot.paperTrading ? 'Paper Trading' : 'Live Trading'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Typography variant="body1">
                        {bot.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Trading Configuration */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Trading Configuration
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Starting Balance
                      </Typography>
                      <Typography variant="body1">
                        {bot.startBalance
                          ? `${bot.startBalance.toLocaleString()} ${bot.baseCurrency || 'USDT'}`
                          : 'N/A'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Coin List
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {bot.coinList && bot.coinList.length > 0 ? (
                          bot.coinList.map((coin: string) => (
                            <Chip key={coin} label={coin} size="small" />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No coins configured
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Created At
                      </Typography>
                      <Typography variant="body1">
                        {bot.createdAt ? new Date(bot.createdAt).toLocaleString() : 'N/A'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body1">
                        {bot.updatedAt ? new Date(bot.updatedAt).toLocaleString() : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Market Settings */}
            {bot.marketSettings && Object.keys(bot.marketSettings).length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Market Settings
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                        fontSize: '0.875rem',
                      }}
                    >
                      {JSON.stringify(bot.marketSettings, null, 2)}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Advanced DB Tab */}
        <TabPanel value={currentTab} index={1}>
          <GenericEntityEditor
            entityType="bot"
            entityId={botId!}
          />
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default BotDetails;

